import React from "react";
import DataList from "components/datatable/DataList";
import useAxios from "hooks/useAxios";
import useAxiosFunction from "hooks/useAxiosFunction";
import { MyConst } from "data/MyConst";
import { RuleRequired, getStrDateTimeShort } from "utils/MyUtils";

const statusOptions = [
  { id: "LAPORAN_BARU", label: "Laporan Baru" },
  { id: "DIPROSES", label: "Diproses" },
  { id: "SELESAI", label: "Selesai" },
];

function showEmpty(value, fallback = "-") {
  const text = value === null || value === undefined ? "" : String(value).trim();
  return text || fallback;
}

function dateTimeText(value) {
  return value ? getStrDateTimeShort(value) : "-";
}

function attachmentLink(filename) {
  if (!filename) return "-";
  const url = `${String(MyConst.URLASET || "").replace(/\/$/, "")}/issue/${filename}`;
  return (
    <a href={url} target="_blank" rel="noreferrer">
      Lihat Lampiran
    </a>
  );
}

function statusBadge(value, col, item) {
  const label = item?.status_label || showEmpty(value);
  const code = String(value || "").toUpperCase();
  const badgeClass =
    code === "SELESAI"
      ? "badge-success"
      : code === "DIPROSES"
        ? "badge-info"
        : "badge-warning";
  return <span className={`badge ${badgeClass}`}>{label}</span>;
}

function reporterInfo(item = {}) {
  const name = showEmpty(item?.pelapor_name || item?.pelapor_username);
  const role = showEmpty(item?.pelapor_group, "");
  const context = showEmpty(item?.context_name, "");
  const detail = [role, context].filter(Boolean).join(" - ");
  return (
    <span>
      {name}
      {detail ? (
        <>
          <br />
          <span className="text-muted">{detail}</span>
        </>
      ) : null}
    </span>
  );
}

function baseColtype(targetOptions = []) {
  const cols = {
    no: { label: "No", width: 40, align: "center", valign: "top" },
    id_laporan_masalah: { type: "hidden", name: "id_laporan_masalah" },
    judul: {
      label: "Judul Masalah/Subjek",
      type: "text",
      name: "judul",
      colsize: 12,
      validation: RuleRequired,
      renderFunc: (val) => showEmpty(val),
      width: 260,
    },
    target_type: {
      label: "Ditujukan Kepada",
      type: "dropdowndinamis",
      name: "target_type",
      colsize: 6,
      options: targetOptions,
      valueId: "id",
      labelId: "label",
      placeholder: "-Pilih Tujuan-",
      validation: RuleRequired,
    },
    target_label: {
      label: "Ditujukan Kepada",
      renderFunc: (val) => showEmpty(val),
      colsize: 12,
      viewsize: 12,
      width: 220,
    },
    deskripsi: {
      label: "Deskripsi / Detail Masalah",
      type: "textarea",
      name: "deskripsi",
      colsize: 12,
      rows: 5,
      validation: RuleRequired,
      renderFunc: (val) => showEmpty(val),
    },
    lampiran_file: {
      label: "Lampiran File",
      type: "filedrop",
      name: "lampiran_file",
      colsize: 12,
      url: "/file/upload",
      dropname: "lampiran",
      data: { location: "issue", name: "lampiran" },
      renderFunc: (val) => attachmentLink(val),
      width: 130,
    },
    status_code: {
      label: "Status",
      type: "dropdown",
      name: "status_code",
      colsize: 4,
      options: statusOptions,
      valueId: "id",
      labelId: "label",
      validation: RuleRequired,
      renderFunc: statusBadge,
      align: "center",
      width: 90,
    },
    status_label: {
      label: "Status",
      renderFunc: (val, col, item) => statusBadge(item?.status_code, col, item),
      colsize: 4,
      viewsize: 4,
      align: "center",
      width: 90,
    },
    pelapor_username: { label: "Username", renderFunc: (val) => showEmpty(val), width: 150 },
    pelapor_name: { label: "Nama Pelapor", renderFunc: (val) => showEmpty(val), width: 190 },
    pelapor_info: { label: "Pelapor", renderFunc: (val, col, item) => reporterInfo(item), width: 260 },
    pelapor_group: { label: "Peran Pelapor", renderFunc: (val) => showEmpty(val), width: 180 },
    context_name: { label: "Ruang Kerja", renderFunc: (val) => showEmpty(val), width: 220 },
    tanggal_laporan: {
      label: "Tanggal Laporan",
      type: "datetime",
      colsize: 4,
      viewsize: 4,
      renderFunc: (val) => dateTimeText(val),
      align: "center",
      width: 115,
    },
    tanggal_selesai: {
      label: "Tanggal Selesai",
      type: "datetime",
      colsize: 4,
      viewsize: 4,
      renderFunc: (val) => dateTimeText(val),
      align: "center",
      width: 115,
    },
    catatan_penyelesaian: {
      label: "Catatan Proses/Penyelesaian",
      type: "textarea",
      name: "catatan_penyelesaian",
      colsize: 12,
      rows: 4,
      renderFunc: (val) => showEmpty(val),
    },
    action: { label: "Aksi", align: "center", width: 90 },
  };

  return Object.fromEntries(
    Object.entries(cols).map(([key, value]) => [key, { ...value, valign: value?.valign || "top" }]),
  );
}

export function IssueReportPage() {
  const [targetResp] = useAxios({ method: "GET", url: "/references/issue-targets", auth: true });
  const targetOptions = Array.isArray(targetResp?.data) ? targetResp.data : [];
  const coltype = baseColtype(targetOptions);

  return (
    <DataList
      title="Laporkan Masalah"
      coltype={coltype}
      dlist={{
        url: "/issues",
        columns: ["no", "judul", "target_label", "status_label", "tanggal_laporan", "tanggal_selesai", "action"],
        filter: [
          { dtype: "q", name: "q", colsize: 4 },
          { dtype: "dropdown", name: "status_code", label: "Status", colsize: 3, options: statusOptions, valueId: "id", labelId: "label", placeholder: "-Semua Status-" },
        ],
        sort_by: ["judul", "target_label", "status_code", "tanggal_laporan", "tanggal_selesai"],
      }}
      addconfig={{
        columns: ["target_type", "judul", "deskripsi", "lampiran_file"],
        title: "",
        label_tool: "Laporkan Masalah",
        url: "/issues",
        size: "lg",
      }}
      detailconfig={{
        url: "/issues",
        cellid: "id_laporan_masalah",
        columns: ["judul", "target_label", "status_label", "tanggal_laporan", "tanggal_selesai", "deskripsi", "lampiran_file", "catatan_penyelesaian"],
        title: "",
        size: "lg",
      }}
      pdflist={false}
      xlslist={false}
    />
  );
}

export function IssueManagementPage() {
  const [, , , axiosFetch] = useAxiosFunction();
  const coltype = baseColtype([]);

  const saveStatus = async (form, defaultValue = {}) => {
    const id = form?.id_laporan_masalah || defaultValue?.id_laporan_masalah || "";
    return axiosFetch({
      method: "PUT",
      url: `/issue-management/${id}`,
      auth: true,
      data: {
        status_code: form.status_code,
        catatan_penyelesaian: form.catatan_penyelesaian || "",
      },
    });
  };

  return (
    <DataList
      title="Laporan Masalah"
      coltype={coltype}
      dlist={{
        url: "/issue-management",
        columns: ["no", "judul", "pelapor_info", "target_label", "status_label", "tanggal_laporan", "action"],
        filter: [
          { dtype: "q", name: "q", colsize: 4 },
          { dtype: "dropdown", name: "status_code", label: "Status", colsize: 3, options: statusOptions, valueId: "id", labelId: "label", placeholder: "-Semua Status-" },
        ],
        sort_by: ["judul", "pelapor_name", "pelapor_group", "context_name", "target_label", "status_code", "tanggal_laporan"],
      }}
      detailconfig={{
        url: "/issue-management",
        cellid: "id_laporan_masalah",
        columns: ["judul", "pelapor_name", "pelapor_username", "pelapor_group", "context_name", "target_label", "status_label", "tanggal_laporan", "tanggal_selesai", "deskripsi", "lampiran_file", "catatan_penyelesaian"],
        title: "",
        size: "lg",
      }}
      editconfig={{
        columns: ["id_laporan_masalah", "judul", "status_code", "catatan_penyelesaian"],
        title: "",
        url: "/issue-management",
        urlitem: "/issue-management",
        cellid: "id_laporan_masalah",
        saveHandle: saveStatus,
        size: "lg",
        coltype: {
          ...coltype,
          judul: { ...coltype.judul, disabled: true },
        },
      }}
      pdflist={{ orientation: "l" }}
      xlslist={{}}
    />
  );
}
